import { Frame, key, Note, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/** compass: wind direction as an arrow, with the speed (and gust) beside it. */
export function Compass({ widget, input }: WidgetProps) {
  const fmt = useWidgetText();
  const by = (role: string) => {
    const b = (widget.bindings ?? []).find((x) => x.role === role && x.datapoint);
    return b?.datapoint ? { b, p: input.points?.[key(b.device, b.datapoint)] } : undefined;
  };
  const dir = by("direction");
  const speed = by("speed");
  const gust = by("gust");
  const deg = dir?.p?.latest?.value;
  const name = widget.title ?? dir?.p?.name ?? "";
  const offline = dir ? input.devices?.[dir.b.device]?.state === "offline" : false;
  if (!dir?.p || typeof deg !== "number") {
    return (
      <Frame widget={widget} state="empty">
        <p aria-label={name} className="text-gray-700">
          {fmt.t("state.noData")}
        </p>
      </Frame>
    );
  }
  const dirText = fmt.value(deg, dir.p);
  const reading = (r?: typeof speed) =>
    r?.p && typeof r.p.latest?.value === "number" ? fmt.value(r.p.latest.value, r.p) : undefined;
  return (
    <Frame widget={widget} state={offline ? "offline" : "normal"} title={widget.title}>
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 40 40" className="size-16" aria-hidden="true">
          <circle cx={20} cy={20} r={18} className="fill-none stroke-gray-300" strokeWidth={2} />
          {/* The arrow points where the wind goes: from `deg`, so rotated by deg + 180. */}
          <path d="M20 6 L25 26 L20 22 L15 26 Z" className="fill-brand" transform={`rotate(${deg + 180} 20 20)`} />
        </svg>
        <div>
          <p aria-label={`${name}: ${dirText}`} className="text-lg font-semibold tabular-nums text-gray-900">
            {dirText}
          </p>
          {reading(speed) && <p className="tabular-nums text-gray-900">{reading(speed)}</p>}
          {reading(gust) && <p className="text-xs tabular-nums text-gray-700">{reading(gust)}</p>}
        </div>
      </div>
      {offline && <Note tone="warn">{fmt.t("state.offline")}</Note>}
    </Frame>
  );
}
